import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Landing } from './Landing';
import { isIDE } from '../lib/isIDE';
import { rootDestination } from './rootDestination';

type Resolution =
  | { kind: 'loading' }
  | { kind: 'to'; path: string };

/**
 * `/` dispatcher. Signed-in users land on their last-active rail surface (or
 * Dashboard). Signed-out users go to /landing in the plain web build, or
 * straight to /sign-in in the IDE — where the marketing page is never shown.
 * Non-blocking — renders nothing (IDE) or Landing (web) while resolving.
 */
export function RootRedirect() {
  const [state, setState] = useState<Resolution>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;

      if (!data.session) {
        setState({
          kind: 'to',
          path: rootDestination({ hasSession: false, activeSurface: null, isIDE: isIDE() })
        });
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('active_surface')
        .eq('id', data.session.user.id)
        .maybeSingle();
      if (cancelled) return;

      setState({
        kind: 'to',
        path: rootDestination({
          hasSession: true,
          activeSurface: (profile?.active_surface ?? null) as string | null,
          isIDE: isIDE()
        })
      });
    }

    resolve();
    return () => {
      cancelled = true;
    };
  }, []);

  // During the async session check: the web app shows Landing immediately
  // (the common signed-out path through `/`). The IDE never shows the
  // marketing page, so it renders nothing until the redirect resolves.
  if (state.kind === 'loading') return isIDE() ? null : <Landing />;
  return <Navigate to={state.path} replace />;
}
